import { css } from '@emotion/react';
import { styled } from '@mui/material/styles';
import {
  Box,
  FormControlLabel,
  InputBase,
  TextField,
  Typography,
} from '@mui/material';
import { APPROVAL_PALETTE } from '@/theme/palette';
import { Body1 } from '@/components/@IntranetLibrary/Typography';
import { MODAL_BASIC_MAX_WIDTH } from '@/components/@IntranetLibrary/Modal/styles';
import {
  VIEW_CONTAINER_PADDING,
  approvalHorizontalScrollbarStyles,
} from '@/components/Approval/ApprovalView/styles';
import { APPROVAL_SECTION_GAP } from '@/components/Approval/@components/InfoSection';

// ===== Layout =====

/** 우측 사이드바(결재라인·참조인·공개레벨) 기본 너비 */
const SIDEBAR_MIN_WIDTH = 384;
/**
 * 사이드바 너비 상한. 화면이 넓어질수록 본문만 늘어나 좌우 비율이 깨지므로
 * 컨테이너의 일정 비율로 함께 키우되, 이 값 이상으로는 늘리지 않는다.
 */
const SIDEBAR_MAX_WIDTH = 520;
/** 사이드바가 컨테이너에서 차지하는 비율. 1600px 부터 기본 너비를 넘어 커지기 시작한다 */
const SIDEBAR_WIDTH_RATIO = '24%';

/**
 * V2 작성 모달 최대 너비.
 * 사이드바(384px)를 더해 본문 영역을 조회 모달만큼 확보하되,
 * 조회에만 있는 문서 컨테이너 여백(조회 모달은 modal-body padding 안에 이 여백이 한 번 더 들어가고,
 * 작성 모달은 full-bleed 라 MainArea padding 만 있음)만큼은 빼서 글이 놓이는 폭을 조회와 같게 맞춘다(약 1064px).
 */
export const NATIVE_APPROVAL_MODAL_MAX_WIDTH =
  MODAL_BASIC_MAX_WIDTH + SIDEBAR_MIN_WIDTH - VIEW_CONTAINER_PADDING * 2;

/** V2 작성 모달에 붙이는 className. 공용 Modal 의 root(MuiModal) 에 전달된다 */
export const NATIVE_APPROVAL_MODAL_CLASS = 'native-approval-modal';

/**
 * V2 작성(ApprovalCreate) 모달 전용 전역 스타일 — basic 크기 모달 최대 너비 확대.
 * 사이드바가 본문 작성 폭을 잠식하지 않게 한다.
 * 본문 여백 제거(full-bleed)는 공용 Modal 의 noBodyPadding 으로 처리한다(확장 시 하단 max-height 해제까지 함께 됨).
 * 확장(expand) 상태는 공용 Modal 이 100% 로 덮어쓰므로 건드리지 않는다.
 * 양식 선택/iframe 레거시 진입에는 className 을 붙이지 않아 적용되지 않는다.
 */
export const nativeApprovalModalStyles = css`
  .${NATIVE_APPROVAL_MODAL_CLASS} .modal-inner.basic {
    max-width: ${NATIVE_APPROVAL_MODAL_MAX_WIDTH}px;
  }
`;

export const Container = styled(Box)(({ theme }) => ({
  height: '100%', // 부모가 가용 높이를 책임짐 (모달: ModalBody, standalone: 페이지 wrapper)
  display: 'flex',
  backgroundColor: 'var(--dk-bg, #fff)',
  // 전자결재 굵은 글씨는 600 — 조회 Container 와 같은 규칙
  '& strong, & b': { fontWeight: 600 },
  ...approvalHorizontalScrollbarStyles,
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    height: 'auto',
    minHeight: '100%',
  },
}));

export const MainArea = styled(Box)(({ theme }) => ({
  flex: 1,
  minWidth: 0,
  padding: 24,
  overflow: 'auto',
  [theme.breakpoints.down('md')]: {
    order: 3,
    overflow: 'visible',
    paddingLeft: 16,
    paddingRight: 16,
    paddingBottom: `calc(24px + env(safe-area-inset-bottom))`,
  },
}));

export const Sidebar = styled(Box)(({ theme }) => ({
  // 좁은 화면에선 기본 너비 고정, 넓은 화면(페이지·확장 모달)에선 본문과 함께 비율로 커진다
  width: `clamp(${SIDEBAR_MIN_WIDTH}px, ${SIDEBAR_WIDTH_RATIO}, ${SIDEBAR_MAX_WIDTH}px)`,
  flexShrink: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
  // 바닥을 회색으로 깔고 섹션을 흰 카드로 띄운다 — 제목줄/내용이 한 덩어리로 읽히게
  padding: 12,
  backgroundColor: 'var(--dk-bg-muted, #f3f5f6)',
  borderLeft: '1px solid var(--dk-line, #e7e9ec)',
  overflow: 'auto',
  [theme.breakpoints.down('md')]: {
    width: '100%',
    borderLeft: 'none',
    borderBottom: '1px solid var(--dk-line, #e7e9ec)',
    order: 2,
    overflow: 'visible',
  },
}));

/** 회색 바닥 위에 뜨는 흰 카드. 결재라인 + 참조인처럼 한 덩어리인 섹션들을 함께 감싼다 */
const SIDEBAR_CARD_RADIUS = 12;
/** 테두리 1px 안쪽이라 자식은 그만큼 작은 반경으로 맞춰야 모서리가 맞물린다 */
const SIDEBAR_CARD_INNER_RADIUS = SIDEBAR_CARD_RADIUS - 1;

export const SidebarCard = styled(Box)({
  backgroundColor: 'var(--dk-bg, #fff)',
  border: '1px solid var(--dk-line, #e7e9ec)',
  borderRadius: SIDEBAR_CARD_RADIUS,
  // 검색 드롭다운이 카드 밖으로 나가야 해서 잘라내지 않는다.
  // 대신 배경을 칠하는 첫/마지막 블록이 카드 모서리를 덮지 않게 라운드를 물려준다.
  overflow: 'visible',
  '& > *:first-of-type > *:first-of-type': {
    borderTopLeftRadius: SIDEBAR_CARD_INNER_RADIUS,
    borderTopRightRadius: SIDEBAR_CARD_INNER_RADIUS,
  },
  '& > *:last-of-type > *:last-of-type': {
    borderBottomLeftRadius: SIDEBAR_CARD_INNER_RADIUS,
    borderBottomRightRadius: SIDEBAR_CARD_INNER_RADIUS,
  },
});

export const MobileHeader = styled(Box)(({ theme }) => ({
  display: 'none',
  [theme.breakpoints.down('md')]: {
    display: 'block',
    padding: '16px 24px',
    borderBottom: '1px solid var(--dk-line, #e7e9ec)',
    order: 1,
  },
}));

export const Header = styled(Box)(({ theme }) => ({
  padding: '16px 0',
  marginBottom: 16,
  [theme.breakpoints.down('md')]: {
    display: 'none',
  },
}));

// ===== Header content =====

export const CreateTitle = styled(Typography)({
  fontSize: 24,
});

// ===== Field blocks =====

export const FieldBlock = styled(Box)({
  marginBottom: APPROVAL_SECTION_GAP,
});

/** 본문 에디터 블록 — 모바일은 넓은 표를 화면 폭으로 누르지 않고 가로 스크롤(조회 화면과 동일) */
export const ContentFieldBlock = styled(FieldBlock)(({ theme }) => ({
  [theme.breakpoints.down('md')]: {
    '& .ContentEditable__root .PlaygroundEditorTheme__table': {
      maxWidth: 'none',
    },
  },
}));

export const AttachmentBlock = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(1),
}));

export const FieldHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(1),
}));

export const SubjectTextField = styled(TextField)`
  & .MuiInputBase-root {
    height: 44px;
    border-radius: 8px;
  }
  /* hover 무반응 / focus 시 검정 라인 — 연관품의 검색창과 동일 패턴 */
  & .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline {
    border-color: var(--dk-line, #e7e9ec);
  }
  /* focus: MUI 기본 2px 파랑 대신 1px 검정 */
  & .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline {
    border-color: #000000;
    border-width: 1px;
  }
`;

// ===== Footer =====

export const Footer = styled(Box)(({ theme }) => ({
  padding: '24px 0 32px',
  width: '100%',
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 16,
  },
}));

export const ActionButtonGroup = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: 8,
  [theme.breakpoints.down('md')]: {
    order: 2,
    width: '100%',
    alignSelf: 'stretch',
    '& > *': {
      flex: 1,
    },
  },
}));

/** 좌측 액션 스택 — 모바일은 display:contents로 풀어 Footer flex item으로 order 재배치 */
export const LeftActionStack = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: 24,
  [theme.breakpoints.down('md')]: {
    display: 'contents',
    '& > *:first-of-type': { order: 1 },
    // 모바일은 취소 버튼이 빠져 템플릿 저장만 남음 — Footer 의 flex-start 를 뒤집어 중앙 배치
    '& > *:last-of-type': { order: 3, alignSelf: 'center' },
  },
}));

// ===== Section shell =====

// 섹션 경계 구분선 — border-bottom 자체가 디바이더라 두께가 곧 디바이더 높이
export const SectionBox = styled(Box)({
  marginBottom: 0,
});

export const SectionDivider = styled(Box)`
  align-self: stretch;
  height: 3px;
  background: var(--dk-bg-muted, #f3f5f6);
`;

export const SectionTitle = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'clickable',
})<{ clickable?: boolean }>(({ clickable = true }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '12px 16px',
  // 접힘·펼침·hover 무관하게 흰색 유지 — hover 틴트(#fafafa)는 펼침 시 SectionContent(#f9fafb)와
  // 구분되지 않아 제목 행까지 한 회색 블록으로 읽힘
  backgroundColor: 'var(--dk-bg, #fff)',
  borderBottom: '1px solid var(--dk-line, #e7e9ec)',
  userSelect: 'none',
  cursor: clickable ? 'pointer' : 'default',
}));

/** Body1 내부 sx 가 이기므로 폰트 속성 추가 금지 — 배치 속성만 */
export const SectionTitleLabel = styled(Body1)`
  display: flex;
  align-items: center;
  gap: 4px;
  /* 우측 요약 텍스트가 길면(en/zh) 제목이 줄바꿈으로 찌그러짐 — 최소폭 확보 */
  min-width: 120px;
`;

/** 헤더 우측(요약 텍스트 + chevron) 묶음 */
export const SectionTitleRight = styled(Box)`
  display: inline-flex;
  align-items: center;
  min-width: 0;
  gap: 8px;
`;

/** 아코디언 접힘 시 선택 옵션 요약 — Figma titleRow 우측 텍스트 스펙 */
export const SectionTitleSummary = styled(Typography)`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--dk-text-secondary, #888d96);
  font-size: 14px;
  font-weight: 500;
  line-height: 24px;
`;

export const SectionChevron = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'open',
})<{ open?: boolean }>`
  display: inline-flex;
  transition: transform 0.2s ease;
  transform: ${({ open }) => (open ? 'rotate(0deg)' : 'rotate(180deg)')};
`;

/**
 * 섹션 여닫기 토글 — 키보드 포커스 대상이 되도록 실제 button.
 * 자체 핸들러 없음: 클릭/Enter 는 부모로 버블 — onClick 을 가진 헤더 컨테이너 안에서만 사용할 것
 */
export const SectionToggleButton = styled('button')`
  display: inline-flex;
  align-items: center;
  padding: 4px;
  border: none;
  background: transparent;
  cursor: pointer;
`;

export const SectionContent = styled(Box)`
  padding: 16px;
  background-color: var(--dk-bg, #fff);
`;

// ===== Secu level — restrict referrer row =====

export const RestrictReferrerRow = styled(Box)`
  display: flex;
  align-items: center;
`;

export const RestrictCheckLabel = styled(FormControlLabel)`
  margin-left: 0;
`;

export const RestrictCheckText = styled(Typography)(({ theme }) => ({
  marginLeft: theme.spacing(0.5),
}));

/** 카드 안에서 한 단계 낮은 제목 — 참조인처럼 상위 섹션에 딸린 블록에 쓴다 */
export const SubSectionLabel = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 16px 16px 0;
  color: var(--dk-text, #000000);
  font-size: 15px;
  font-weight: 500;
  line-height: 22px;
`;

/** 서브 라벨 좌측(아이콘 + 글자) 묶음 */
export const SubSectionLabelText = styled('span')`
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

/** 제목줄 우측 '최근 결재라인' 진입점 — chevron(접기)·↻(초기화)와 겹치지 않게 밑줄 텍스트만 */
export const HistoryEntryButton = styled('button')`
  border: none;
  background: transparent;
  padding: 0;
  color: var(--dk-text-strong, #4d5157);
  font-family: inherit;
  font-size: 13px;
  font-weight: 500;
  line-height: 20px;
  text-decoration: underline;
  text-underline-offset: 2px;
  cursor: pointer;

  &:hover {
    color: var(--dk-text, #000);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
    border-radius: 2px;
  }
`;

/* ── 최근 결재라인 카드 — 제목 · 순번 칩 · 구성 요약 ──────────────── */

export const CardTitleRow = styled(Box)`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
`;

export const CardTitle = styled('span')`
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--dk-text, #000);
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
`;

export const CardDate = styled('span')`
  flex-shrink: 0;
  color: var(--dk-text-secondary, #888d96);
  font-size: 12px;
  line-height: 18px;
`;

export const CardMetaRow = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
`;

export const CardAvatarStack = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'inlineFlow',
})<{ inlineFlow?: boolean }>(({ inlineFlow = false }) =>
  inlineFlow
    ? {
        // 묶음 칩이 두 줄이 되어도 통짜로 부풀지 않게 인라인 흐름을 쓴다
        display: 'block',
        width: '100%',
        minWidth: 0,
        lineHeight: '26px',
        '& > *': { verticalAlign: 'middle' },
        '& > * + *': { marginLeft: 4 },
      }
    : {
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 4,
        width: '100%',
        minWidth: 0,
      },
);

/**
 * 얼굴 + 이름 한 덩어리 — 사진만으로는 누군지 알기 어려워 이름을 함께 둔다.
 * 협조자는 채움 대신 테두리 + 앞머리 아이콘으로 결재자와 구분한다.
 */
export const CardPersonChip = styled('span', {
  shouldForwardProp: (prop) => prop !== 'tone' && prop !== 'wide',
})<{ tone?: 'approver' | 'coop'; wide?: boolean }>(
  ({ tone = 'approver', wide = false }) => {
    const isCoop = tone === 'coop';
    return {
      ...(wide
        ? null
        : { display: 'inline-flex', alignItems: 'center', gap: 4 }),
      minWidth: 0,
      // 협조 묶음은 이름이 여럿이라 남은 줄 폭을 끝까지 쓴다
      ...(wide
        ? {
            // 줄바꿈되면 각 줄이 따로 둥근 배경을 갖는다
            display: 'inline',
            maxWidth: 'none',
            WebkitBoxDecorationBreak: 'clone',
            boxDecorationBreak: 'clone',
            // 인라인 배경은 글자 높이 기준이라 안의 번호 박스가 아래로 치우친다 — 1px 올려 균형을 맞춘다
            '& > *': { verticalAlign: 'middle', position: 'relative', top: -1 },
            '& .chip-no': { marginLeft: 6 },
            '& .chip-nm': { marginLeft: 4 },
            '& .chip-sep': { marginLeft: 4 },
            '& .chip-out': { marginLeft: 6 },
            '& > *:first-of-type': { marginLeft: 0 },
          }
        : { maxWidth: 132 }),
      padding: wide ? '3px 8px 3px 4px' : '2px 8px 2px 2px',
      borderRadius: 999,
      // 테두리를 두르면 눌리는 것처럼 보이므로 쓰지 않는다 — 강약은 배경 톤으로만
      background: isCoop ? '#e2e6ea' : '#f3f5f6',
      color: 'var(--dk-text-secondary, #888d96)',
      ...(isCoop ? { paddingLeft: 6 } : null),
    };
  },
);

/** 퇴사자 표시 — 적용하면 빠지는 사람이라 이름 앞에서 미리 알린다 */
export const CardPersonDeactivated = styled('span')`
  flex-shrink: 0;
  padding: 0 4px;
  border-radius: 4px;
  background: var(--dk-danger-soft, #fdd5d9);
  color: #b3261e;
  font-size: 10px;
  font-weight: 500;
  line-height: 16px;
  white-space: nowrap;
`;

/** 협조 묶음에서 번호 없이 이름만 이어질 때 경계 — 동시 처리 전용 */
export const CardGroupSep = styled('span')`
  color: var(--dk-text-secondary, #a8aeb6);
  font-size: 12px;
  line-height: 18px;
`;

/** 칩 앞머리 순번 — 사이드바 결재라인의 번호 박스와 같은 언어 */
export const CardChipNo = styled('span')`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  min-width: 18px;
  height: 18px;
  padding: 0 4px;
  border-radius: 6px;
  border: 1px solid var(--dk-line, #dfe3e8);
  background: var(--dk-bg, #fff);
  color: var(--dk-text-primary, #34383d);
  font-size: 11px;
  font-weight: 500;
  line-height: 16px;
  font-variant-numeric: tabular-nums;
`;

/** 협조자 칩 앞머리 아이콘 자리 */
export const CardPersonCoopMark = styled('span')`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  color: var(--dk-text-strong, #4d5157);
`;

export const CardPersonName = styled('span')`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--dk-text-primary, #34383d);
  font-size: 12px;
  line-height: 18px;
`;

export const CardAvatar = styled('span')`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: var(--dk-bg-strong, #e7e9ec);
  overflow: hidden;
  color: var(--dk-text-secondary, #888d96);

  & img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

export const CardAvatarMore = styled('span')`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  color: var(--dk-text-secondary, #888d96);
  font-size: 12px;
  line-height: 18px;
  white-space: nowrap;
`;

export const CardSummaryText = styled('span')`
  color: var(--dk-text-secondary, #888d96);
  font-size: 12px;
  line-height: 18px;
`;

/** '최근 결재라인' 팝오버 — 목록·검색을 상시 노출에서 빼낸다 */
export const HistoryPopoverPaper = styled(Box)`
  width: 360px;
  max-height: 70vh;
  overflow: auto;
  border: 1px solid var(--dk-line, #e7e9ec);
  border-radius: 12px;
  background: var(--dk-bg, #fff);
  box-shadow: 0 8px 24px rgba(20, 28, 36, 0.14);
`;

/** 팝오버 머리말 — 제목과 닫기 */
export const HistoryPopoverHeader = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px 0;
  color: var(--dk-text, #000);
  font-size: 15px;
  font-weight: 600;
  line-height: 22px;
`;

export const HistoryPopoverClose = styled('button')`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: var(--dk-text-strong, #4d5157);
  cursor: pointer;

  &:hover {
    background: var(--dk-bg-muted, #f3f5f6);
  }
`;

// ===== Approval line — toolbar =====

export const ApprovalLineToolbar = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  background-color: var(--dk-bg, #fff);
  user-select: none;
  cursor: pointer;
`;

/** 흰 SegmentTabIndicator 가 위를 슬라이드하므로 트랙은 회색 유지 */
export const SegmentTabs = styled(Box)`
  position: relative;
  display: flex;
  align-items: center;
  padding: 4px;
  gap: 4px;
  border-radius: 8px;
  background-color: var(--dk-bg-subtle, #f9fafb);
`;

/** 선택 탭 배경 — 탭 아래에서 left/top/width/height 로 슬라이드 */
export const SegmentTabIndicator = styled('span')`
  position: absolute;
  z-index: 0;
  border-radius: 4px;
  background-color: var(--dk-bg, #fff);
  box-shadow: 0 0 4px 0 rgba(0, 0, 0, 0.08);
  pointer-events: none;
  transition: left 240ms cubic-bezier(0.4, 0, 0.2, 1),
    top 240ms cubic-bezier(0.4, 0, 0.2, 1),
    width 240ms cubic-bezier(0.4, 0, 0.2, 1),
    height 240ms cubic-bezier(0.4, 0, 0.2, 1);

  @media (prefers-reduced-motion: reduce) {
    transition-duration: 0ms;
  }
`;

export const SegmentTab = styled('button')`
  position: relative;
  z-index: 1;
  padding: 3px 8px;
  border: none;
  border-radius: 4px;
  background-color: transparent;
  cursor: pointer;
`;

export const SegmentTabText = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'active',
})<{ active?: boolean }>(({ active }) => ({
  fontSize: 12,
  lineHeight: '18px',
  fontWeight: 400,
  color: active ? '#000000' : '#888D96',
}));

// ===== Approval line — recent/my tab title-search bar =====

/** '제목으로 검색' 입력 영역 — 아래 패딩은 다음 컨텐츠가 자체 보유 */
export const RecentSearchSection = styled(Box)`
  padding: 16px 16px 0;
  background-color: var(--dk-bg, #fff);
`;

export const RecentSearchBar = styled(Box)`
  display: flex;
  align-items: center;
  gap: 12px;
  height: 44px;
  padding: 12px 12px 12px 16px;
  border: 1px solid var(--dk-line, #e7e9ec);
  border-radius: 8px;
  background-color: var(--dk-bg, #fff);
  transition: border-color 120ms ease;

  &:focus-within {
    border-color: #000;
  }
`;

export const RecentSearchInput = styled(InputBase)`
  flex: 1;
  font-size: 14px;
  line-height: 20px;
  padding: 0;
  && .MuiInputBase-input {
    padding: 0;
  }
  & input::placeholder {
    color: var(--dk-text-dim, #b7bbc2);
    opacity: 1;
  }
`;

export const RecentSearchButton = styled('button')`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  color: var(--dk-text, #000);

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
    border-radius: 2px;
  }
`;

// ===== Approval line — empty history =====

export const EmptyHistoryContent = styled(SectionContent)(({ theme }) => ({
  paddingTop: theme.spacing(5),
  paddingBottom: theme.spacing(5),
  textAlign: 'center',
  backgroundColor: 'var(--dk-bg, #fff)',
}));

/** 40x40 아이콘 + 본문 텍스트의 빈 상태. 검색 결과 없음/데이터 없음 두 케이스 공용. */
export const EmptyHistoryWithIcon = styled(Box)`
  padding: 24px 16px;
  background-color: var(--dk-bg, #fff);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
`;

/** 빈 상태 아이콘 슬롯 — currentColor 사용 svg(no-data 아이콘 등)를 위해 color 유지 */
export const EmptyHistoryIconWrap = styled(Box)`
  width: 40px;
  height: 40px;
  color: var(--dk-text-disabled, #d4d7db);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  & svg {
    width: 100%;
    height: 100%;
  }
`;

export const EmptyHistoryText = styled(Typography)`
  color: var(--dk-text-dim, #b7bbc2);
`;

// ===== Approval line — history list (recent / my) =====

export const HistoryListContent = styled(SectionContent)`
  display: flex;
  flex-direction: column;
  gap: 8px;
  background-color: var(--dk-bg, #fff);
`;

// ===== Approval line — history pagination =====
// HistoryListContent 안에 들어가는 row — 배경/좌우 padding은 부모(content)에서 상속.

export const HistoryListPagination = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
`;

export const HistoryListPaginationButton = styled('button')`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  padding: 0;
  border: 1px solid var(--dk-line, #e7e9ec);
  border-radius: 4px;
  background-color: var(--dk-bg, #fff);
  color: var(--dk-text-secondary, #888d96);
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;

  &:hover:not(:disabled) {
    background-color: var(--dk-bg-muted, #f3f5f6);
    color: var(--dk-text, #000);
  }

  &:disabled {
    cursor: not-allowed;
    color: var(--dk-text-disabled, #d4d7dc);
  }

  &:focus-visible {
    outline: 2px solid #000000;
    outline-offset: 2px;
  }
`;

export const HistoryListPaginationLabel = styled('span')`
  display: inline-flex;
  align-items: center;
  padding: 0 8px;
  font-family: Pretendard, sans-serif;
  font-size: 13px;
  font-weight: 400;
  line-height: 18px;
  color: var(--dk-text-secondary, #888d96);
`;

/** 숫자 슬롯 — 최소 2자리(2ch) 공간 확보, 가운데 정렬. 더 큰 숫자는 자동으로 늘어남. */
export const HistoryListPaginationNumber = styled('span')`
  display: inline-block;
  min-width: 2ch;
  text-align: center;
`;

export const HistoryListPaginationCurrent = styled(HistoryListPaginationNumber)`
  color: var(--dk-text, #000);
`;

/** "/" 슬래시 — inline-block 경계에서 양쪽 공백 collapse 방지용 명시 padding. */
export const HistoryListPaginationDivider = styled('span')`
  padding: 0 4px;
`;

/** chip 행 + 제목/날짜를 함께 담는 보더 박스. */
export const HistoryListItem = styled('div', {
  shouldForwardProp: (prop) => prop !== 'active' && prop !== 'clickable',
})<{ active?: boolean; clickable?: boolean }>(({ active, clickable }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  gap: 8,
  width: '100%',
  padding: 12,
  border: '1px solid var(--dk-line, #e7e9ec)',
  borderRadius: 8,
  backgroundColor: 'var(--dk-bg, #fff)',
  overflow: 'hidden',
  textAlign: 'left',
  cursor: clickable ? 'pointer' : 'default',
  transition: 'border-color 0.15s ease',

  // popover/drawer가 떠 있는 동안엔 마우스가 popover로 넘어가도 카드 강조 유지
  ...(active ? { borderColor: '#000' } : {}),

  ...(clickable
    ? {
        '&:hover': {
          borderColor: '#000',
        },
        '&:focus-visible': {
          outline: '2px solid #000',
          outlineOffset: 2,
        },
      }
    : {}),
}));

/**
 * 결재라인 흐름의 협조 단계 chip.
 * surface='popup' 이 피그마 값. 기존 컴포넌트 값은 그대로 두고 분기
 */
export const HistoryListItemBadgeCooperation = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'surface',
})<{ surface?: 'card' | 'popup' }>(({ surface = 'card' }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  padding: surface === 'popup' ? 4 : '5px 4px',
  minHeight: 28,
  border: '1px solid var(--dk-accent-line, #c2daf3)',
  borderRadius: 4,
  backgroundColor: 'var(--dk-accent-soft, #ebf3fb)',
  fontFamily: 'Pretendard, sans-serif',
  fontSize: 12,
  fontWeight: 400,
  lineHeight: surface === 'popup' ? '18px' : '16px',
  color: 'var(--dk-accent, #0066cc)',
  whiteSpace: 'nowrap',
}));

export const HistoryListItemBadgeCooperationText = styled('span')`
  display: inline-block;
  max-width: 30px;
  padding: 0 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const HistoryListItemFooterKebab = styled('button')`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  background: transparent;
  color: var(--dk-text-secondary, #888d96);
  cursor: pointer;
  border-radius: 4px;

  &:hover {
    background-color: var(--dk-bg-strong, #e7e9ec);
    color: var(--dk-text, #000);
  }

  &:focus-visible {
    outline: 2px solid #000000;
    outline-offset: 2px;
  }
`;

// ===== Approval line — history expanded detail (popover/drawer) =====

/** 데스크탑 호버 popover Paper — 카드 아래 bottom-start */
export const HistoryDetailPopoverPaper = styled(Box)`
  width: 360px;
  max-width: calc(100vw - 32px);
  background-color: var(--dk-bg, #fff);
  border-radius: 8px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
  overflow: hidden;
`;

/** 데스크탑 popover 전용 헤더 */
export const HistoryDetailSheetHeader = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 16px 12px 20px;
  background-color: rgba(249, 250, 251, 0.9);
  backdrop-filter: blur(8px);
  border-bottom: 1px solid var(--dk-line, #e7e9ec);
`;

export const SheetHeader = styled(Box)`
  display: grid;
  grid-template-columns: 28px 1fr 28px;
  align-items: center;
  gap: 8px;
  height: 56px;
  padding: 0 16px;
  background-color: var(--dk-bg, #fff);
  border-bottom: 1px solid var(--dk-line, #e7e9ec);
`;

export const SheetHeaderTitle = styled('span')`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: center;
  font-family: Pretendard, sans-serif;
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  color: var(--dk-text, #000);
`;

export const SheetHeaderIconButton = styled('button')`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: none;
  background: transparent;
  color: var(--dk-text, #000);
  cursor: pointer;
  border-radius: 4px;

  &:active {
    background-color: var(--dk-bg-muted, #f3f5f6);
  }
  &:focus-visible {
    outline: 2px solid #000000;
    outline-offset: 2px;
  }
`;

export const SheetFooter = styled(Box)`
  display: flex;
  padding: 12px 20px;
  background-color: var(--dk-bg-muted, #f3f5f6);
  border-top: 1px solid var(--dk-line, #e7e9ec);

  & > button {
    flex: 1;
  }
`;

export const HistoryDetailSheetTitle = styled('span')`
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-family: Pretendard, sans-serif;
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  color: var(--dk-text, #000);
`;

/**
 * 헤더 케밥 + 드롭다운 anchor.
 * 메뉴는 popover paper 안 absolute 유지 — 포탈로 빼면 paper mouseleave 가 떠서
 * 메뉴 고르는 중 popover 가 닫힘.
 */
export const HistoryDetailMenuAnchor = styled(Box)`
  position: relative;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
`;

export const HistoryDetailMenuRoot = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'align',
})<{ align?: 'left' | 'right' }>(({ align = 'right' }) => ({
  position: 'absolute',
  top: 30,
  ...(align === 'left' ? { left: 0 } : { right: 0 }),
  zIndex: 1,
}));

export const HistoryDetailSheetKebab = styled('button')`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  background: transparent;
  color: var(--dk-text, #000);
  cursor: pointer;
  border-radius: 2px;

  &:hover {
    background-color: var(--dk-bg-strong, #e7e9ec);
    color: var(--dk-text, #000);
  }
  &:focus-visible {
    outline: 2px solid #000000;
    outline-offset: 2px;
  }
`;

/** popup body — 컨테이너 padding 24 안에서 divider 까지 gap 12 */
export const HistoryDetailContents = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'compact',
})<{ compact?: boolean }>(({ compact }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
  padding: compact ? '24px 20px 12px 20px' : '24px 24px 12px 24px',
  backgroundColor: 'var(--dk-bg, #fff)',
}));

export const HistoryDetailSection = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

/** 결재라인 wrap — chevron이 chip에 attach된 approveItem들이 wrap. row gap 8, col gap 0 */
export const HistoryDetailApproverWrap = styled(Box)`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  align-content: center;
  row-gap: 8px;
  column-gap: 0;
  width: 100%;
`;

/** 참조인 wrap — chip 사이에 chevron 없음. gap 8 (양방향) */
export const HistoryDetailReferrerWrap = styled(Box)`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  align-content: center;
  gap: 8px;
  width: 100%;
`;

/** approver+arrow 묶음 (wrap 시 chevron이 chip에 붙어 같이 줄바꿈되도록) */
export const HistoryDetailApproverItem = styled(Box)`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
`;

/** 결재라인 흐름 chip 사이 chevron wrapper */
export const HistoryDetailArrowIcon = styled('span')`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  color: var(--dk-text-secondary, #888d96);
`;

/** Figma chip: 28h 4p, 20px 원형 아바타 + 4px 패딩 + 12px 이름 */
export const HistoryDetailAvatarChip = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'tone',
})<{ tone?: 'default' | 'cooperation' }>(({ tone = 'default' }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  flexShrink: 0,
  padding: 4,
  borderRadius: 4,
  border: `1px solid ${tone === 'cooperation' ? '#c2daf3' : '#e7e9ec'}`,
  backgroundColor: tone === 'cooperation' ? '#fff' : '#f9fafb',
}));

export const HistoryDetailAvatar = styled('span')`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 40px;
  overflow: hidden;
  flex-shrink: 0;
  background-color: var(--dk-bg-strong, #e7e9ec);
  color: var(--dk-text-secondary, #888d96);

  & > img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

export const HistoryDetailChipText = styled('span')`
  display: -webkit-box;
  padding: 0 4px;
  max-width: 80px;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-all;
  font-family: Pretendard, sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
  color: var(--dk-text, #000);
`;

export const HistoryDetailChipDeactivated = styled('span')`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  padding: 0 4px;
  border-radius: 2px;
  background-color: var(--dk-danger-soft, #fef1f2);
  color: var(--dk-danger, #f55060);
  font-size: 12px;
  line-height: 16px;
`;

/** 협조 결재 박스 — 라벨을 박스 안에 품는 독립 섹션(결재라인 흐름의 협조 chip 과 별개) */
export const HistoryDetailCoopBox = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  padding: 8px;
  border: 1px solid var(--dk-accent-line, #c2daf3);
  border-radius: 4px;
  background-color: var(--dk-accent-soft, #ebf3fb);
`;

export const HistoryDetailCoopChips = styled(Box)`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  align-content: center;
  gap: 8px;
  width: 100%;
`;

/** 아래 두 개는 데스크탑 popover 전용. 모바일은 SheetFooter */
export const HistoryDetailDivider = styled('div')`
  height: 1px;
  margin: 0 24px;
  background-color: var(--dk-bg-strong, #e7e9ec);
`;

export const HistoryDetailButtonRow = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  padding: 12px 24px 24px 24px;
`;

// ===== Approval line — search =====

export const SearchSectionContent = styled(SectionContent)(({ theme }) => ({
  padding: theme.spacing(2),
  backgroundColor: 'var(--dk-bg, #fff)',
}));

export const SearchBar = styled(Box)`
  display: flex;
  align-items: center;
  align-self: stretch;
  gap: 12px;
  height: 44px;
  padding: 8px 12px 8px 8px;
  border: 1px solid var(--dk-line, #e7e9ec);
  border-radius: 8px;
  background-color: var(--dk-bg, #fff);
  transition: border-color 120ms ease;

  &:focus-within {
    border-color: #000;
  }
`;

/** SearchBar + 결과 드롭다운 앵커 — absolute 드롭다운용 relative 컨텍스트 */
export const BottomSearchPositioner = styled(Box)`
  position: relative;
`;

export const SearchInput = styled(InputBase)`
  flex: 1;
  font-size: 14px;
  line-height: 24px;
  padding: 0;
  /* 테마 전역 input padding 12px 오버라이드 — 패딩은 외곽 SearchBar 담당 */
  && .MuiInputBase-input {
    padding: 0;
  }
  & input::placeholder {
    color: var(--dk-text-dim, #b7bbc2);
    opacity: 1;
  }
`;

export const SearchIconWrapper = styled(Box)`
  flex-shrink: 0;
  display: inline-flex;
`;

// ===== Approval line — action row (초기화 / 나의 결재라인에 추가) =====

export const ApprovalLineActionRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: '8px 16px 0',
  backgroundColor: 'var(--dk-bg, #fff)',
});

export const AddBoxButton = styled('button')`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 36px;
  padding: 0 12px;
  border: 1px solid var(--dk-line, #e7e9ec);
  border-radius: 6px;
  background: var(--dk-bg, #ffffff);
  color: var(--dk-text-primary, #34383d);
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  cursor: pointer;

  &:hover {
    background: var(--dk-bg-subtle, #f9fafb);
  }
`;

// ===== Approval line — flow list =====

export const FlowList = styled(Box)`
  position: relative;
  padding: 8px 16px;
  background-color: var(--dk-bg, #fff);

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 16px;
    right: 16px;
    height: 1px;
    background-color: var(--dk-bg-strong, #e7e9ec);
  }

  /* ───────────────────────────────────────────────────────────────
     협조 범위 표현 — 파란 색면 대신 협조자 카드들만 한 덩어리 회색 블록으로.
     번호 열은 블록 밖에 남아 일반 결재자와 같은 열에 정렬된다.
     ─────────────────────────────────────────────────────────────── */
  & .coop-block .flow-cover {
    background: var(--dk-bg-subtle, #f4f5f7);
  }
  /* 배경이 사람 카드로 옮겨왔으므로 블록 안쪽 오른쪽 여백은 걷어
     일반 결재자 카드와 오른쪽 끝을 맞춘다 */
  & .coop-block .flow-row {
    padding-right: 0;
  }
  /* 블록 자식 순서: 위 이음선 · 헤더 · 멤버들 · 아래 여백 */
  & .coop-block > div:nth-child(3) .flow-cover {
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
  }
  & .coop-block > div:nth-last-child(2) .flow-cover {
    border-bottom-left-radius: 8px;
    border-bottom-right-radius: 8px;
  }
  /* 끌어다 놓는 동안 — 검정 테두리는 튀므로 묶음 색만 한 톤 올린다 */
  & .coop-block[data-drop-target] .flow-cover {
    background: var(--dk-bg-strong, #e7e9ec);
  }
`;

const FLOW_RAIL_WIDTH = 28;
/** 결재자를 잇는 세로선의 x (행 왼쪽 기준) */
const FLOW_RAIL_CENTER = 22;
/** 협조 블록 padding 1px 만큼 당겨야 밖 세로선과 한 줄 */
const COOP_RAIL_CENTER = FLOW_RAIL_CENTER - 1;
const COOP_INNER_INSET = 12;
const FLOW_ROW_GAP = 4;
const FLOW_INSERT_SLOT_HEIGHT = 16;
/** 이름 카드가 시작하는 x (세로선 자리 다음) */
const FLOW_COVER_LEFT =
  FLOW_RAIL_CENTER - FLOW_RAIL_WIDTH / 2 + FLOW_RAIL_WIDTH + FLOW_ROW_GAP;

export const FlowRow = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== 'isDragging' && prop !== 'variant' && prop !== 'isDragSource',
})<{
  isDragging?: boolean;
  isDragSource?: boolean;
  variant?: 'normal' | 'cooperation';
}>`
  display: flex;
  align-items: stretch;
  gap: ${FLOW_ROW_GAP}px;
  padding-left: ${({ variant }) =>
    (variant === 'cooperation' ? COOP_RAIL_CENTER : FLOW_RAIL_CENTER) -
    FLOW_RAIL_WIDTH / 2}px;
  padding-right: ${({ variant }) =>
    variant === 'cooperation' ? COOP_INNER_INSET : 0}px;
  border-radius: 8px;
  width: 100%;
  transition: background 120ms ease;

  & .approve-user-card {
    border: 1px solid transparent;
    transition: border-color 120ms ease, background 120ms ease;
  }

  & .row-close,
  & .row-swap {
    opacity: 0;
    pointer-events: none;
    transition: opacity 120ms ease;
  }

  & .approve-user-card:hover,
  & .approve-user-card:focus-within,
  &:has(.number-head:hover) .approve-user-card,
  &:has(.number-head:focus-visible) .approve-user-card {
    border-color: var(--dk-line, #e7e9ec);
    background: var(--dk-bg, #ffffff);
  }

  &:has(.number-head:active) .approve-user-card {
    border-color: var(--dk-line, #e7e9ec);
    background: ${({ variant }) =>
      variant === 'cooperation'
        ? 'var(--dk-bg-strong, #e7e9ec)'
        : 'var(--dk-bg-muted, #f3f5f6)'};
  }

  &:has(.approve-user-card:hover) .number-text,
  &:has(.approve-user-card:focus-within) .number-text,
  &:has(.number-head:hover) .number-text,
  &:has(.number-head:focus-visible) .number-text {
    display: none;
  }

  &:has(.approve-user-card:hover) .drag-handle,
  &:has(.approve-user-card:focus-within) .drag-handle,
  &:has(.number-head:hover) .drag-handle,
  &:has(.number-head:focus-visible) .drag-handle {
    display: inline-flex;
  }

  &:has(.approve-user-card:hover) :is(.row-close, .row-swap),
  &:has(.approve-user-card:focus-within) :is(.row-close, .row-swap) {
    opacity: 1;
    pointer-events: auto;
  }

  &:has(.number-head:hover) :is(.row-close, .row-swap),
  &:has(.number-head:active) :is(.row-close, .row-swap),
  &:has(.number-head:focus-visible) :is(.row-close, .row-swap) {
    opacity: 0;
    pointer-events: none;
  }

  &:has(.row-close:hover) .approve-user-card,
  &:has(.row-close:active) .approve-user-card {
    background: var(--dk-danger-soft, #fef1f2);
    border-color: var(--dk-danger-line, #fdd5d9);
  }

  /* 카드는 사본이 그리니 자리만 남긴다.
     번호 박스는 감추기만 하면 세로선에 구멍이 나므로 레이아웃에서 뺀다. */
  ${({ isDragSource }) =>
    isDragSource
      ? `
      pointer-events: none;

      & .flow-cover {
        opacity: 0;
      }

      & .number-head {
        display: none;
      }
    `
      : ''}

  ${({ isDragging, variant }) => {
    if (!isDragging) return '';
    const isCoop = variant === 'cooperation';
    const cardBorder = '#e7e9ec';
    const cardBg = isCoop ? '#e7e9ec' : '#f3f5f6';
    const numBorder = '#34383D';
    const numBg = '#e7e9ec';
    return `
      && .approve-user-card {
        border-color: ${cardBorder};
        background: ${cardBg};
      }
      && .number-head {
        border-color: ${numBorder};
        background: ${numBg};
      }
      && .number-head .number-text {
        display: none;
      }
      && .number-head .drag-handle {
        display: inline-flex;
      }
      &&& :is(.row-close, .row-swap) {
        opacity: 0;
        pointer-events: none;
      }
    `;
  }}

  ${({ theme }) => theme.breakpoints.down('md')} {
    & .row-close,
    & .row-swap {
      display: none;
    }
  }
`;

export const FlowRail = styled(Box)`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: ${FLOW_RAIL_WIDTH}px;
  flex-shrink: 0;
  align-self: stretch;
`;

export const FlowRailSegment = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'variant' && prop !== 'hidden',
})<{ variant?: 'normal' | 'cooperation'; hidden?: boolean }>(
  ({ hidden = false }) => ({
    position: 'relative',
    flex: '1 1 0',
    width: '100%',
    minHeight: 0,
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      bottom: 0,
      left: '50%',
      width: 1,
      backgroundColor: hidden ? 'transparent' : '#E7E9EC',
    },
  }),
);

export const FlowRailTop = styled(FlowRailSegment)(({ theme }) => ({
  flex: '0 0 14px',
  minHeight: 14,
  [theme.breakpoints.down('md')]: {
    flex: '0 0 16px',
    minHeight: 16,
  },
}));

export const FlowNumberHead = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'variant' && prop !== 'active',
})<{ variant?: 'normal' | 'cooperation'; active?: boolean }>(
  ({ active = false }) => {
    const defaultBorder = active ? '#0066CC' : '#E7E9EC';
    const hoverBorder = active ? '#0066CC' : '#34383D';
    const pressedBg = active ? '#FFFFFF' : '#E7E9EC';
    return {
      position: 'relative',
      zIndex: 1,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 4,
      width: 28,
      height: 28,
      borderRadius: 8,
      border: `1px solid ${defaultBorder}`,
      background: 'var(--dk-bg, #FFFFFF)',
      color: 'var(--dk-text, #000000)',
      fontFamily: 'var(--font-Pretendard)',
      fontSize: 14,
      fontWeight: 500,
      lineHeight: '20px',
      textAlign: 'center',
      flexShrink: 0,
      boxSizing: 'border-box',
      cursor: active ? 'pointer' : 'grab',
      transition: 'border-color 120ms ease, background 120ms ease',
      '& .number-text': {
        display: 'inline-flex',
      },
      '& .drag-handle': {
        display: 'none',
      },
      '&:hover': {
        borderColor: hoverBorder,
      },
      '&:active': {
        background: pressedBg,
        borderColor: hoverBorder,
        cursor: active ? 'pointer' : 'grabbing',
      },
    };
  },
);

export const FlowCover = styled(Box)`
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: stretch;
`;

export const FlowUserCard = styled(Box)`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px 8px 8px;
  border-radius: 8px;
  width: 100%;
  cursor: grab;
  transition: background 120ms ease;

  &:active {
    cursor: grabbing;
  }

  & button,
  & input {
    cursor: auto;
  }

  & button[aria-label='결재자 제외'],
  & button[aria-label='더보기'] {
    cursor: pointer;
  }
`;

export const FlowAvatar = styled(Box)`
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border-radius: 999px;
  overflow: hidden;
  background: var(--dk-bg-muted, #f3f5f6);

  & > img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

export const FlowMemberInfo = styled(Box)(({ theme }) => ({
  flex: '1 1 auto',
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  [theme.breakpoints.down('md')]: {
    gap: 4,
  },
}));

export const FlowMemberNameRow = styled(Box)`
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
  width: 100%;
  color: var(--dk-text, #000000);
  font-size: 16px;
  font-weight: 500;
  line-height: 20px;
  white-space: nowrap;

  & > .name-text {
    flex: 1 1 auto;
    min-width: 0;
    display: inline-flex;
    align-items: center;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

export const FlowKebabButton = styled('button')(({ theme }) => ({
  display: 'none',
  flexShrink: 0,
  width: 16,
  height: 16,
  padding: 0,
  border: 'none',
  background: 'transparent',
  color: 'var(--dk-text-secondary, #888D96)',
  cursor: 'pointer',
  [theme.breakpoints.down('md')]: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  '&:hover': {
    color: 'var(--dk-text-primary, #34383D)',
  },
}));

export const FlowCloseButton = styled('button')`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: var(--dk-danger, #f55060);
  cursor: pointer;
  transition: background 120ms ease;

  &:hover {
    background: var(--dk-danger-soft, #fde3e6);
  }

  &:active {
    background: var(--dk-danger-soft, #fdd5d9);
  }
`;

export const FlowSwapButton = styled('button')`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: var(--dk-text-secondary, #888d96);
  cursor: pointer;
  transition: background 120ms ease, color 120ms ease;

  &:hover {
    background: var(--dk-bg-muted, #f3f5f6);
    color: var(--dk-text-primary, #34383d);
  }
`;

/** 드래그 중 결재 유형이 바뀌는 위치에 있을 때 카드 위에 붙는 표시 */
export const DragTypeChangeBadge = styled('span')`
  position: absolute;
  top: -10px;
  right: 8px;
  z-index: 1;
  padding: 2px 8px;
  border-radius: 10px;
  background: var(--dk-text-primary, #34383d);
  color: var(--dk-bg, #ffffff);
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
  white-space: nowrap;
  pointer-events: none;
`;

export const DragOverlayFrame = styled('div')`
  position: relative;
`;

export const FlowMemberName = styled('span')`
  font-weight: 500;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const FlowMemberPosition = styled('span')`
  margin-left: 4px; /* 이름과 직급 사이 공백 확보 */
  font-weight: 500;
`;

export const FlowMemberDept = styled(Box)`
  display: flex;
  gap: 4px;
  width: 100%;
  color: var(--dk-text-secondary, #888d96);
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  white-space: nowrap;

  & > .department {
    flex: 1 1 auto;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

export const FlowBottomSpace = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'hidden',
})<{ hidden?: boolean }>(({ hidden = false }) => ({
  height: hidden ? 0 : 12,
  width: '100%',
  flexShrink: 0,
}));

export const FlowInlineAddSlot = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'variant',
})<{ variant?: 'normal' | 'cooperation' }>(({ theme, variant = 'normal' }) => ({
  position: 'relative',
  height: FLOW_INSERT_SLOT_HEIGHT,
  width: '100%',
  borderRadius: 2,
  background: 'transparent',
  flexShrink: 0,
  transition: 'background 120ms ease',
  '& > button': {
    opacity: 0,
    transition: 'opacity 120ms ease',
    pointerEvents: 'none',
  },
  '&:hover': {
    // 협조 박스 배경과 같은 색이면 안 보여서 박스 안에서만 한 톤 진하게
    background: variant === 'cooperation' ? '#D6E7F7' : '#EBF3FB',
    zIndex: 10,
  },
  '&:has(> button:active)': {
    zIndex: 10,
  },
  '&:hover > button': {
    opacity: 1,
    pointerEvents: 'auto',
  },
  [theme.breakpoints.down('md')]: {
    background: 'transparent',
    '&:hover': {
      background: 'transparent',
    },
    '& > button, &:hover > button': {
      display: 'none',
    },
  },
}));

export const FlowInlineAddButton = styled('button', {
  shouldForwardProp: (prop) => prop !== 'variant',
})<{ variant?: 'normal' | 'cooperation' }>({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 28,
  height: 28,
  padding: 4,
  // 결재/협조결재 동일 — 파란 톤으로 통일
  border: '1px solid var(--dk-accent-line, #d6e7f7)',
  borderRadius: 8,
  background: 'var(--dk-bg, #ffffff)',
  color: 'var(--dk-accent, #0066cc)',
  boxSizing: 'border-box',
  transition: 'background 120ms ease, border-color 120ms ease',
  // Figma "Pressed - Insert" 시각을 hover/active 모두에 적용 — + 버튼 자체 인터랙션 강조
  '&:hover, &:active': {
    cursor: 'pointer',
    background: 'var(--dk-accent-soft, #d6e7f7)',
    borderColor: 'var(--dk-accent, #0066cc)',
  },
});

export const FlowInlineSearch = styled(Box)`
  position: relative;
  flex: 1;
  display: flex;
  align-items: center;
  gap: 8px;
  height: 40px;
  padding: 0 12px;
  border: 1px solid #000000;
  border-radius: 6px;
  background: var(--dk-bg, #ffffff);

  & input {
    flex: 1;
    border: none;
    outline: none;
    font-size: 14px;
    line-height: 20px;
    background: transparent;
    color: var(--dk-text, #1a1a1a);
    min-width: 0;
  }

  & input::placeholder {
    color: var(--dk-text-dim, #b7bbc2);
  }
`;

export const FlowSearchClearButton = styled('button')`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  padding: 0;
  border: none;
  background: transparent;
  color: var(--dk-text-secondary, #888d96);
  cursor: pointer;
  flex-shrink: 0;

  &:hover {
    color: var(--dk-text-primary, #34383d);
  }
`;

export const FlowSearchDropdown = styled(Box)`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  z-index: 20;
  display: flex;
  flex-direction: column;
  max-height: 260px;
  padding: 8px;
  border: 1px solid #000000;
  border-radius: 8px;
  background: var(--dk-bg, #ffffff);
  box-shadow: 0 16px 32px 0 rgba(0, 0, 0, 0.08);
  overflow-y: auto;
`;

export const FlowSearchDropdownHeader = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'muted',
})<{ muted?: boolean }>(({ muted = false }) => ({
  padding: 8,
  // 결과 라벨은 검정, '결과 없음'·'몇 자 이상' 같은 안내 문구는 회색
  color: muted ? '#888d96' : '#1a1a1a',
  fontSize: 13,
  fontWeight: muted ? 400 : 500,
  lineHeight: '18px',

  '& > .count': {
    color: 'var(--dk-accent, #0066cc)',
    marginLeft: 4,
  },
}));

export const FlowSearchOption = styled('button')`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: var(--dk-text, #1a1a1a);
  text-align: left;
  cursor: pointer;
  min-width: 0;

  &:hover,
  &:focus {
    background: var(--dk-bg-muted, #f3f5f6);
    outline: none;
  }
`;

export const FlowSearchOptionAvatar = styled(Box)`
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border-radius: 999px;
  overflow: hidden;
  background: var(--dk-bg-muted, #f3f5f6);

  & > img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

export const FlowSearchOptionInfo = styled(Box)`
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
`;

export const FlowSearchOptionName = styled(Box)`
  color: var(--dk-text, #1a1a1a);
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  & > .match {
    color: var(--dk-accent, #0066cc);
  }
`;

export const FlowSearchOptionDept = styled(Box)`
  color: var(--dk-text-secondary, #888d96);
  font-size: 13px;
  line-height: 18px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

// ===== Approval line — cooperation block =====

export const CooperationBlock = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isDragging',
})<{ isDragging?: boolean }>(({ isDragging = false }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  width: '100%',
  // 범위는 안쪽 카드 묶음이 말하므로 블록 자체는 테두리·배경을 갖지 않는다
  // border 는 배율에 따라 디바이스 픽셀로 스냅돼 안쪽 세로선이 어긋나므로 padding 유지
  padding: 1,
  transition: 'background 120ms ease',
  ...(isDragging && {
    '& .approve-user-card': {
      background: 'var(--dk-bg-strong, #e7e9ec)',
    },
  }),
}));

/** hidden 이어도 8px 공간은 유지 — 첫 번째 위치일 때 라인만 숨김(FlowRailTop 과 동일 규칙) */
export const CooperationTopRail = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'hidden',
})<{ hidden?: boolean }>`
  position: relative;
  height: 8px;
  width: 100%;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: ${COOP_RAIL_CENTER}px;
    width: 1px;
    background: ${({ hidden }) =>
      hidden ? 'transparent' : 'var(--dk-bg-strong, #e7e9ec)'};
  }
`;

const COOP_HANDLE_SIZE = 32;

/** 협조 박스 아래 여백. 뒤에 결재자가 있으면 세로선도 이어 그린다 */
export const CooperationInnerBottom = styled('div', {
  shouldForwardProp: (prop) => prop !== 'showRail',
})<{ showRail?: boolean }>`
  position: relative;
  height: ${COOP_INNER_INSET}px;
  width: 100%;
  flex-shrink: 0;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: ${COOP_RAIL_CENTER}px;
    width: 1px;
    background: ${({ showRail }) =>
      showRail ? 'var(--dk-bg-strong, #e7e9ec)' : 'transparent'};
  }
`;

/** 협조 박스 다음 결재자를 추가하는 바. 협조 멤버가 아니라서 박스 밖에 둔다 */
export const CooperationAfterSlot = styled('div')`
  position: relative;
  height: ${FLOW_INSERT_SLOT_HEIGHT}px;
  width: 100%;
  flex-shrink: 0;
  padding-left: ${FLOW_COVER_LEFT}px;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: ${FLOW_RAIL_CENTER}px;
    width: 1px;
    background: var(--dk-bg-strong, #e7e9ec);
  }
`;

export const CooperationHeader = styled(Box)`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 ${COOP_INNER_INSET}px 0
    ${COOP_RAIL_CENTER - COOP_HANDLE_SIZE / 2}px;
  border-radius: 8px;
  width: 100%;
`;

export const CooperationHeaderLeft = styled(Box)`
  flex: 1 1 auto;
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

export const CooperationHeaderIcon = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isDragging',
})<{ isDragging?: boolean }>(({ isDragging = false }) => ({
  position: 'relative',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: COOP_HANDLE_SIZE,
  height: COOP_HANDLE_SIZE,
  borderRadius: 8,
  border: `1px solid ${isDragging ? '#34383D' : '#E7E9EC'}`,
  background: isDragging ? '#F3F5F6' : '#FFFFFF',
  color: isDragging ? '#34383D' : '#4D5157',
  flexShrink: 0,
  cursor: 'grab',
  transition: 'background 120ms ease, border-color 120ms ease',
  touchAction: 'none',
  '& .coop-icon-layer': {
    position: 'absolute',
    inset: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    pointerEvents: 'none',
    transition: 'opacity 120ms ease',
  },
  '& .coop-profiles': {
    opacity: isDragging ? 0 : 1,
  },
  '& .coop-step-num': {
    fontFamily: 'var(--font-Pretendard)',
    fontSize: 14,
    fontWeight: 500,
    lineHeight: '20px',
    textAlign: 'center',
  },
  '& .coop-drag-handle': {
    opacity: isDragging ? 1 : 0,
  },
  '&:hover': {
    background: 'var(--dk-bg-subtle, #F9FAFB)',
    borderColor: '#4D5157',
    color: 'var(--dk-text-strong, #4D5157)',
    '& .coop-profiles': { opacity: 0 },
    '& .coop-drag-handle': { opacity: 1 },
  },
  '&:active': {
    background: 'var(--dk-bg-muted, #F3F5F6)',
    borderColor: '#34383D',
    color: 'var(--dk-text-primary, #34383D)',
    cursor: 'grabbing',
    '& .coop-profiles': { opacity: 0 },
    '& .coop-drag-handle': { opacity: 1 },
  },
}));

export const CooperationHeaderTitle = styled(Typography)`
  color: var(--dk-text, #000000);
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const OrderToggleLabel = styled(FormControlLabel)`
  margin: 0;
  gap: 4px;

  & .MuiFormControlLabel-label {
    color: var(--dk-text, #000000);
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
  }

  & .MuiCheckbox-root {
    padding: 0;
    width: 20px;
    height: 20px;
  }
`;

/* ---------- 결재라인: 모바일 케밥 바텀 액션시트 ---------- */

export const KebabSheetContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px 0 48px;
`;

export const KEBAB_SHEET_ICON_BOX = 28;

/** close.svg 만 viewBox 가 16(나머지는 20) — 프레임과 같은 값을 주면 혼자 커 보임 */
export const KEBAB_SHEET_CLOSE_ICON = 20;
/** 기본 1.5 를 두면 획만 굵어짐 */
export const KEBAB_SHEET_CLOSE_STROKE = 1;

export const KebabSheetItem = styled('button', {
  shouldForwardProp: (prop) => prop !== 'danger',
})<{ danger?: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 8px 20px;
  border: none;
  background: transparent;
  font-family: inherit;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: ${({ danger }) =>
    danger ? APPROVAL_PALETTE.error.main : 'var(--dk-text, #000000)'};
  cursor: pointer;
  text-align: left;
  &:active {
    background-color: var(--dk-bg-muted, #f3f5f6);
  }
`;

/** 글리프 px 이 달라도 라벨 시작 위치가 같도록 폭 고정 */
export const KebabSheetItemIcon = styled('span')`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: ${KEBAB_SHEET_ICON_BOX}px;
  height: ${KEBAB_SHEET_ICON_BOX}px;
`;
